/**
 * ebookalia — Course landing behaviour.
 *
 * Two jobs, both purely presentational:
 *   1. Reveal sections as they scroll into view.
 *   2. Show a sticky purchase bar once the hero is out of view, and get it out
 *      of the way again when the final CTA arrives so it never covers content.
 *
 * No commerce logic lives here. Purchases go through Shopify's native product
 * form, which works with this script disabled.
 *
 * Robustness note: the reveal must never be able to leave sales copy
 * permanently invisible. IntersectionObserver callbacks are suspended while a
 * tab is hidden (and can be missed if another app's script throws during
 * startup), so a cheap geometry sweep backs the observer up and runs again
 * whenever the page becomes visible.
 */
(function () {
  'use strict';

  // Every cl-* section renders the shared asset snippet, so this file is
  // referenced several times on the page. Only the first execution should set
  // anything up.
  if (window.__ebookaliaCourseLanding) return;
  window.__ebookaliaCourseLanding = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsObserver = 'IntersectionObserver' in window;
  var ticking = false;

  function reveal(el) {
    el.classList.add('is-in');
  }

  /** True once the GSAP layer has taken ownership of every entrance. */
  function gsapOwnsAnimation() {
    return document.documentElement.classList.contains('cl-gsap');
  }

  /** Reveal anything already intersecting the viewport. Safety net for the observer. */
  function sweepReveal() {
    if (gsapOwnsAnimation()) return;

    var pending = document.querySelectorAll('.cl-reveal:not(.is-in)');
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    for (var i = 0; i < pending.length; i++) {
      var rect = pending[i].getBoundingClientRect();
      if (rect.top < viewportHeight * 0.92 && rect.bottom > 0) reveal(pending[i]);
    }
  }

  function initReveal() {
    // course-cinematic.js runs first and flags itself on <html>. When it is in
    // charge, this observer must stay out of the way so the two engines never
    // animate the same properties.
    if (gsapOwnsAnimation()) return;

    var targets = document.querySelectorAll('.cl-reveal:not(.is-in)');
    if (!targets.length) return;

    // The storefront is intentionally visible by default. Arm the CSS reveal
    // only once this local fallback is ready to reveal visible content.
    document.documentElement.classList.add('cl-anim');

    if (reducedMotion || !supportsObserver) {
      for (var i = 0; i < targets.length; i++) reveal(targets[i]);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    targets.forEach(function (target) {
      observer.observe(target);
    });
  }

  /**
   * Sticky bar visibility, computed straight from geometry rather than from
   * observer callbacks so it stays correct after tab switches and soft
   * navigations.
   */
  function initStickyBar() {
    var bar = document.querySelector('[data-cl-sticky]');
    if (!bar) return;

    var trigger = document.querySelector('[data-cl-sticky-trigger]');
    if (!trigger) return;

    var stopper = document.querySelector('[data-cl-sticky-stop]');

    function sync() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      // Show only once the hero has fully scrolled off the top.
      var pastTrigger = trigger.getBoundingClientRect().bottom <= 0;

      // Hide again as soon as the final CTA is reached, and keep it hidden for
      // everything below it, so the bar never sits on top of the closing
      // content or the footer.
      var atStopper = stopper ? stopper.getBoundingClientRect().top <= viewportHeight : false;

      var show = pastTrigger && !atStopper;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    bar.__clSync = sync;
    sync();
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      sweepReveal();
      var bar = document.querySelector('[data-cl-sticky]');
      if (bar && bar.__clSync) bar.__clSync();
    });
  }

  function init() {
    initReveal();
    initStickyBar();
    sweepReveal();
  }

  function bind() {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) onScroll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      bind();
    });
  } else {
    init();
    bind();
  }

  // The theme editor swaps section markup in place; re-scan when that happens.
  document.addEventListener('shopify:section:load', init);
})();
